import type { TFunction } from 'i18next'
import type { BotState, XYZ } from '@apiary/shared'

/**
 * Форматирование данных панели. Всё, что показывает числа, обязано быть
 * стабильным по ширине (docs/10 §1.2: tabular-nums, «не прыгает»).
 */

/** `120 / 64 / -310`; координаты округляем до целых блоков. */
export function formatCoords(pos: XYZ | null | undefined): string | null {
  if (pos === null || pos === undefined) return null
  return `${Math.round(pos.x)} / ${Math.round(pos.y)} / ${Math.round(pos.z)}`
}

/** `3д 04ч` / `12ч 40м` / `40м` — как в макете docs/10 §3.1. */
export function formatUptime(
  startedAt: string | null | undefined,
  t: TFunction,
  now = Date.now()
): string | null {
  if (startedAt === null || startedAt === undefined) return null
  const started = Date.parse(startedAt)
  if (Number.isNaN(started)) return null
  const totalMinutes = Math.max(0, Math.floor((now - started) / 60_000))
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return t('bot.uptimeValue', { days, hours: pad2(hours) })
  if (hours > 0) return t('bot.uptimeHoursValue', { hours, minutes: pad2(minutes) })
  return t('bot.uptimeMinutesValue', { minutes })
}

/** `13:42` — обратный отсчёт для device-code (docs/10 §3.4). */
export function formatCountdown(
  expiresAt: string | null | undefined,
  now = Date.now()
): string | null {
  if (expiresAt === null || expiresAt === undefined) return null
  const expires = Date.parse(expiresAt)
  if (Number.isNaN(expires)) return null
  const totalSeconds = Math.max(0, Math.floor((expires - now) / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${pad2(minutes)}:${pad2(seconds)}`
}

/** `21:04:11` — время строки лога, локальная зона. */
export function formatLogTime(at: string): string {
  const date = new Date(at)
  if (Number.isNaN(date.getTime())) return '--:--:--'
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
}

export function formatDateTime(at: string | null | undefined, locale: string): string | null {
  if (at === null || at === undefined) return null
  const date = new Date(at)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })
}

/** `99.2` — процент без «дрожания» третьего знака. */
export function formatPercent(value: number | null | undefined): string | null {
  if (value === null || value === undefined || Number.isNaN(value)) return null
  return value.toFixed(1)
}

/** Код device-flow с интервалами: `KTB4Q7R2` → `K T B 4  Q 7 R 2`. */
export function formatDeviceCode(code: string): string {
  const clean = code.replace(/\s+/g, '')
  const head = clean.slice(0, 4).split('').join(' ')
  const tail = clean.slice(4).split('').join(' ')
  return tail === '' ? head : `${head}  ${tail}`
}

/** `spectrum-03.papyrus.vip:52808`. */
export function formatAddress(host: string, port: number): string {
  return `${host}:${port}`
}

/**
 * Бот сейчас в игре, то есть у него есть мир вокруг.
 *
 * `DEGRADED` сюда входит: это «играет, но с оговорками» (лаги, потери пакетов),
 * а не «не в игре» — мини-карта и камера для него так же осмысленны. Раньше оба
 * экрана фильтровали по одному `PLAYING`, и у бота с оговорками карта пропадала
 * без всякого объяснения.
 */
export function isOnServer(state: BotState): boolean {
  return state === 'PLAYING' || state === 'DEGRADED'
}

/** Семантическая группа состояния — один источник для цвета и иконки. */
export type BotStateTone = 'online' | 'degraded' | 'connecting' | 'waiting' | 'offline' | 'danger'

export function botStateTone(state: BotState): BotStateTone {
  switch (state) {
    case 'PLAYING':
      return 'online'
    case 'DEGRADED':
      return 'degraded'
    case 'CONNECTING':
    case 'LOGIN':
    case 'CONFIG':
    case 'SPAWNING':
      return 'connecting'
    case 'QUEUED':
    case 'RECONNECT_WAIT':
      return 'waiting'
    case 'AUTH_FAILED':
      return 'danger'
    case 'STOPPED':
    case 'UNKNOWN':
      return 'offline'
  }
}

function pad2(value: number): string {
  return value.toString().padStart(2, '0')
}
